// SP6 — builder-first expression editor foundation: a structured BuilderModel
// that maps 1:1 onto the small subset of formula-expression syntax the admin
// UI's clause builder can represent, plus a serializer and a parse-back path
// that reuses the existing rules-engine parser. Pure — no DOM, no Node deps.
//
// Representable subset: a single comparison clause, or a chain of clauses
// joined by exactly one operator kind (all `&&` or all `||`). Each clause's
// sides are a literal, a bare/dotted field ref, or a TXN_* aggregate call
// (optionally with a single-condition same-collection filter). Anything
// outside that subset (mixed joiners, arithmetic composition, multi-condition
// filters, non-TXN functions, unparsable text) is not representable and
// `expressionToBuilder` returns `null` rather than guessing.
import { parse } from "./parser/parser.js";
import { TXN_COLLECTIONS } from "./transaction-context.js";
const COMPARISON_OPS = new Set(["==", "!=", ">", ">=", "<", "<="]);
// --- Serialization ---------------------------------------------------------
function serializeLiteral(value) {
    return typeof value === "string" ? `'${value}'` : String(value);
}
function serializeSide(side) {
    switch (side.kind) {
        case "ref":
            return side.name;
        case "literal":
            return serializeLiteral(side.value);
        case "aggregate": {
            const fieldPart = side.field ? `.${side.field}` : "";
            const filterPart = side.filter
                ? `, ${side.collection}.${side.filter.field} ${side.filter.op} ${serializeLiteral(side.filter.value)}`
                : "";
            return `${side.fn}(${side.collection}${fieldPart}${filterPart})`;
        }
    }
}
function serializeClause(clause) {
    return `${serializeSide(clause.left)} ${clause.op} ${serializeSide(clause.right)}`;
}
export function builderToExpression(model) {
    const joinerStr = model.joiner === "AND" ? " && " : " || ";
    return model.clauses.map(serializeClause).join(joinerStr);
}
// --- Parse-back --------------------------------------------------------
function mapAggregate(node) {
    const fn = node.name;
    if (node.args.length !== 1 && node.args.length !== 2)
        return null;
    const first = node.args[0];
    if (first.type !== "fieldRef")
        return null;
    const dot = first.name.indexOf(".");
    const head = dot < 0 ? first.name : first.name.slice(0, dot);
    if (!TXN_COLLECTIONS.has(head))
        return null;
    const collection = head;
    let field;
    if (fn === "TXN_COUNT") {
        if (dot >= 0)
            return null; // TXN_COUNT takes a bare collection name
    }
    else {
        if (dot < 0)
            return null; // TXN_SUM/MIN/MAX require collection.key
        field = first.name.slice(dot + 1);
    }
    let filter;
    if (node.args.length === 2) {
        const second = node.args[1];
        if (second.type !== "binaryOp" || !COMPARISON_OPS.has(second.op))
            return null;
        if (second.left.type !== "fieldRef")
            return null;
        const fdot = second.left.name.indexOf(".");
        if (fdot < 0)
            return null;
        const fhead = second.left.name.slice(0, fdot);
        if (fhead !== collection)
            return null; // filter must reference the same collection
        if (second.right.type !== "literal")
            return null;
        if (typeof second.right.value === "boolean" || second.right.value === null)
            return null;
        filter = { field: second.left.name.slice(fdot + 1), op: second.op, value: second.right.value };
    }
    if (fn === "TXN_COUNT") {
        return filter
            ? { kind: "aggregate", fn, collection, filter }
            : { kind: "aggregate", fn, collection };
    }
    return filter
        ? { kind: "aggregate", fn, collection, field: field, filter }
        : { kind: "aggregate", fn, collection, field: field };
}
function mapSide(node) {
    switch (node.type) {
        case "literal":
            if (node.value === null)
                return null; // a null literal is not a representable side
            return { kind: "literal", value: node.value };
        case "fieldRef":
            return { kind: "ref", name: node.name };
        case "functionCall":
            if (!node.name.startsWith("TXN_"))
                return null;
            return mapAggregate(node);
        case "binaryOp":
        case "unaryOp":
            return null; // arithmetic/logical composition is not a representable side
    }
}
function mapClause(node) {
    if (node.type !== "binaryOp" || !COMPARISON_OPS.has(node.op))
        return null;
    const left = mapSide(node.left);
    if (!left)
        return null;
    const right = mapSide(node.right);
    if (!right)
        return null;
    return { left, op: node.op, right };
}
/** Flatten a left-leaning chain of one operator kind (`&&`-only or
 *  `||`-only) into its leaf nodes, in source order. A node that isn't such
 *  a chain (e.g. a single clause) yields itself as the sole leaf. */
function flattenChain(ast) {
    if (ast.type === "binaryOp" && (ast.op === "&&" || ast.op === "||")) {
        const targetOp = ast.op;
        const leaves = [];
        let cur = ast;
        while (cur.type === "binaryOp" && cur.op === targetOp) {
            leaves.unshift(cur.right);
            cur = cur.left;
        }
        leaves.unshift(cur);
        return { joiner: targetOp === "&&" ? "AND" : "OR", leaves };
    }
    return { joiner: "AND", leaves: [ast] };
}
export function expressionToBuilder(expr) {
    let ast;
    try {
        ast = parse(expr);
    }
    catch {
        return null;
    }
    const { joiner, leaves } = flattenChain(ast);
    const clauses = [];
    for (const leaf of leaves) {
        const clause = mapClause(leaf);
        if (!clause)
            return null;
        clauses.push(clause);
    }
    if (clauses.length === 0)
        return null;
    const model = { joiner, clauses };
    // Safety net: only representable if re-serializing lands back on the
    // input exactly (guards against un-escapable string literals, alternate
    // spacing/parenthesization, and any other shape this mapper missed).
    let reserialized;
    try {
        reserialized = builderToExpression(model);
    }
    catch {
        return null;
    }
    if (reserialized !== expr.trim())
        return null;
    return model;
}
//# sourceMappingURL=expression-builder.js.map